import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register plugins
gsap.registerPlugin( ScrollTrigger );

// Function to preload images
function preloadImage( src ) {
	return new Promise( ( resolve, reject ) => {
		if ( ! src ) {
			reject( new Error( 'No source provided' ) );
			return;
		}

		const img = new Image();
		img.onload = () => resolve( img );
		img.onerror = ( err ) => {
			console.error( `Failed to load image: ${ src }`, err );
			reject( err );
		};
		img.src = src;
	} );
}

document.addEventListener( 'DOMContentLoaded', () => {
	// DIAGNOSTIC: Log all section IDs and data-section attributes to help with debugging
	console.log( 'SCROLL ANIMATION DIAGNOSTICS:' );
	const allSections = document.querySelectorAll( '.scroll-section' );
	const allNavLinks = document.querySelectorAll( '.nav-link' );

	// Get all section images for preloading
	const sectionImages = Array.from(
		document.querySelectorAll( '.scroll-section img' )
	);
	const imageData = sectionImages.map( ( img ) => ( {
		element: img,
		src: img.getAttribute( 'src' ),
		index: parseInt( img.dataset.sectionIndex, 10 ) || 0,
	} ) );

	// Log all images found for debugging
	console.log( 'Images found:', imageData.length );
	imageData.forEach( ( imgData, i ) => {
		console.log(
			`Image ${ i }: index=${ imgData.index }, src=${
				imgData.src ? 'present' : 'missing'
			}`
		);
	} );

	console.log( 'Section IDs:' );
	allSections.forEach( ( section ) => {
		console.log( `- ${ section.id }` );
	} );

	console.log( 'Nav link data-section attributes:' );
	allNavLinks.forEach( ( link ) => {
		console.log(
			`- ${ link.getAttribute(
				'data-section'
			) } (href: ${ link.getAttribute( 'href' ) })`
		);
	} );

	const mm = gsap.matchMedia();

	mm.add( '(min-width: 1024px)', () => {
		const container = document.querySelector(
			'.scroll-animation-container'
		);
		const sections = document.querySelectorAll( '.scroll-section' );

		if ( ! container || sections.length === 0 ) return;
		const navLinks = document.querySelectorAll( '.nav-link' );
		// Initialize navigation indicators on page load
		navLinks.forEach( ( link, i ) => {
			const indicator = link.querySelector( '.nav-indicator' );
			if ( ! indicator ) return;

			// Set initial state for all indicators
			gsap.set( indicator, {
				width: i === 0 ? '100%' : '0',
				transformOrigin: 'left center',
			} );

			// Set initial active class for first item
			if ( i === 0 ) {
				link.classList.add( 'active', 'text-mc-blue-400' );
				link.classList.remove( 'text-mc-black-100' );
			}
		} );

		function updateNavigation( self ) {
			const progress = self.progress;
			const sectionIndex = Math.min(
				Math.floor( progress * sections.length ),
				sections.length - 1
			);

			// Preload next section's image if available
			if ( sectionIndex + 1 < sections.length ) {
				// Find and preload the next 2 images
				[ 1, 2 ].forEach( ( offset ) => {
					const nextIndex = sectionIndex + offset;
					if ( nextIndex < sections.length ) {
						const nextImage = imageData.find(
							( img ) => img.index === nextIndex
						);
						if ( nextImage && nextImage.src ) {
							// Add a small timeout to not block main thread
							setTimeout( () => {
								preloadImage( nextImage.src ).catch( ( err ) =>
									console.warn(
										`Failed to preload image at index ${ nextIndex }: ${
											err.message || 'Unknown error'
										}`
									)
								);
							}, 100 );
						}
					}
				} );
			}

			navLinks.forEach( ( link, i ) => {
				const indicator = link.querySelector( '.nav-indicator' );
				if ( ! indicator ) return;

				if ( i === sectionIndex ) {
					link.classList.add( 'active', 'text-mc-blue-400' );
					link.classList.remove( 'text-mc-black-100' );
					gsap.to( indicator, { width: '100%', duration: 0.3 } );
				} else {
					link.classList.remove( 'active', 'text-mc-blue-400' );
					link.classList.add( 'text-mc-black-100' );
					gsap.to( indicator, { width: '0', duration: 0.3 } );
				}
			} );
		}

		// Set up timeline
		const scrollTween = gsap.timeline( {
			scrollTrigger: {
				trigger: container,
				pin: true,
				start: 'top top',
				end: `+=${ sections.length * 100 }%`,
				scrub: 3,
				onUpdate: updateNavigation,
			},
		} );

		// Account for the -8vh offset in the animations
		const yOffset = window.innerHeight * -0.02;

		// Set initial state
		gsap.set( sections[ 0 ], { visibility: 'visible', opacity: 1 } );

		// Add animations for each section
		sections.forEach( ( section, index ) => {
			const textContent = section.querySelector( '.text-container' );

			// Preload image for next section when this section becomes active
			if ( index < sections.length - 1 ) {
				scrollTween.call(
					() => {
						// Log which section is becoming active
						console.log(
							`Section ${ index } becoming active, preloading next images`
						);

						// Preload next images if available
						[ 1, 2 ].forEach( ( offset ) => {
							const nextIndex = index + offset;
							if ( nextIndex < sections.length ) {
								const nextImg = sectionImages[ nextIndex ];
								if ( nextImg && nextImg.src ) {
									console.log(
										`Preloading image for section ${ nextIndex }`
									);
									preloadImage( nextImg.src ).catch(
										( err ) =>
											console.warn(
												`Failed to preload image for section ${ nextIndex }: ${
													err.message ||
													'Unknown error'
												}`
											)
									);
								} else {
									console.warn(
										`No image found for section ${ nextIndex }`
									);
								}
							}
						} );
					},
					[],
					index * 0.8
				); // Call earlier to ensure images are loaded in time
			}

			// Animate current section - slowed down
			if ( index > 0 ) {
				scrollTween.fromTo(
					section,
					{ visibility: 'hidden', opacity: 0 },
					{
						visibility: 'visible',
						opacity: 1,
						duration: 0.8,
					}
				);
			}

			// Text animation - slowed down
			const textElements = [
				textContent.querySelector( '.eyebrow' ),
				textContent.querySelector( 'h2' ),
				textContent.querySelector( 'h3' ),
				textContent.querySelector( '.content' ),
				textContent.querySelector( '.o-button' ),
			];

			scrollTween.fromTo(
				textElements,
				{ y: 50 + yOffset, opacity: 0 },
				{
					y: yOffset,
					opacity: 1,
					stagger: 1,
					duration: 2.5,
					ease: 'power2.out',
				}
			);

			// Keep section visible for longer
			scrollTween.to( {}, { duration: 2 } );

			// Animate text out - slowed down
			scrollTween.to( textElements, {
				y: -50 + yOffset,
				opacity: 0,
				stagger: 1,
				duration: 2.5,
				ease: 'power2.in',
			} );

			// Transition to next section - quick
			if ( index < sections.length - 1 ) {
				scrollTween.to( section, {
					visibility: 'hidden',
					opacity: 0,
					duration: 0.3,
				} );
			}
		} );

		// Handle navigation clicks
		navLinks.forEach( ( link ) => {
			link.addEventListener( 'click', ( e ) => {
				e.preventDefault();

				// Try data-section first, fall back to href without the # if needed
				let targetId = link.getAttribute( 'data-section' );
				if ( ! targetId ) {
					const href = link.getAttribute( 'href' );
					if ( href && href.startsWith( '#' ) ) {
						targetId = href.substring( 1 );
					}
				}

				if ( ! targetId ) return;

				const targetSection = document.getElementById( targetId );

				if ( ! targetSection ) {
					console.warn(
						`Target section with ID ${ targetId } not found`
					);
					return;
				}

				// Preload image for the target section
				const targetIndex =
					Array.from( sections ).indexOf( targetSection );

				// Find the target section's image directly
				const targetImg = targetSection.querySelector( 'img' );
				if ( targetImg && targetImg.src ) {
					console.log(
						`Navigation: Preloading image for section ${ targetIndex }`
					);
					preloadImage( targetImg.src ).catch( ( err ) =>
						console.warn(
							`Navigation: Failed to preload target image: ${
								err.message || 'Unknown error'
							}`
						)
					);
				}

				// Find the index of the target section
				const index = Array.from( sections ).indexOf( targetSection );

				// Calculate progress point in the timeline
				const progress = index / ( sections.length - 1 || 1 );

				// Get ScrollTrigger instance
				const scrollTriggerInstance = ScrollTrigger.getAll().find(
					( st ) => st.trigger === container
				);

				if ( scrollTriggerInstance ) {
					const scrollPos =
						scrollTriggerInstance.start +
						( scrollTriggerInstance.end -
							scrollTriggerInstance.start ) *
							progress;

					console.log(
						`Scrolling to section ${ targetId } at position ${ scrollPos }`
					);
					// Adjust scrollPos to account for any offset
					window.scrollTo( {
						top: scrollPos,
						behavior: 'smooth',
					} );
				}
			} );
		} );

		// Add 3D hover effects to the images
		sections.forEach( ( section ) => {
			const image = section.querySelector( '.image-container' );

			section.addEventListener( 'mousemove', ( e ) => {
				const rect = section.getBoundingClientRect();
				const x = ( e.clientX - rect.left ) / rect.width - 0.5;
				const y = ( e.clientY - rect.top ) / rect.height - 0.5;

				gsap.to( image, {
					rotationY: x * 15,
					rotationX: -y * 15,
					transformPerspective: 1000,
					ease: 'power2.out',
					duration: 2,
				} );
			} );

			section.addEventListener( 'mouseleave', () => {
				gsap.to( image, {
					rotationY: 0,
					rotationX: 0,
					ease: 'power2.out',
					duration: 2,
				} );
			} );
		} );

		// Add parallax effect to images
		sections.forEach( ( section ) => {
			const image = section.querySelector( 'img' );

			gsap.fromTo(
				image,
				{ y: '0' },
				{
					y: '-10%',
					ease: 'power2.inOut',
					scrollTrigger: {
						trigger: section,
						containerAnimation: scrollTween,
						scrub: 3,
						start: 'top bottom',
						end: 'bottom top',
					},
				}
			);
		} );
	} );

	mm.add( '(max-width: 1023px)', () => {
		const sections = document.querySelectorAll( '.scroll-section' );
		const navLinks = document.querySelectorAll( '.nav-link' );

		// For mobile, add Intersection Observer to lazy load images
		const imagesToLoad = document.querySelectorAll(
			'.scroll-section img[loading="lazy"]'
		);

		if ( 'IntersectionObserver' in window ) {
			const imageObserver = new IntersectionObserver(
				( entries, observer ) => {
					entries.forEach( ( entry ) => {
						if ( entry.isIntersecting ) {
							const img = entry.target;
							// Force load by setting src
							if ( img.dataset.src ) {
								img.src = img.dataset.src;
								img.removeAttribute( 'data-src' );
							}
							imageObserver.unobserve( img );
						}
					} );
				},
				{
					rootMargin: '200px 0px', // Start loading when image is 200px from viewport
					threshold: 0.01,
				}
			);

			imagesToLoad.forEach( ( img ) => {
				imageObserver.observe( img );
			} );
		}

		sections.forEach( ( section ) => {
			gsap.set( section, {
				position: 'relative',
				visibility: 'visible',
				opacity: 1,
			} );
		} );

		// Get the navigation container
		const navContainer = document.querySelector( '.scroll-nav ul' );

		// Function to scroll nav item into view
		function scrollNavIntoView( navLink ) {
			if ( ! navContainer || ! navLink ) return;

			// Get the nav item's position relative to the container
			const navItemRect = navLink.getBoundingClientRect();
			const containerRect = navContainer.getBoundingClientRect();

			// Calculate the scroll position to center the nav item
			const scrollLeft =
				navContainer.scrollLeft +
				navItemRect.left -
				containerRect.left -
				containerRect.width / 2 +
				navItemRect.width / 2;

			// Smoothly scroll the navigation
			navContainer.scrollTo( {
				left: scrollLeft,
				behavior: 'smooth',
			} );
		}

		// Use Intersection Observer for section detection
		const observerOptions = {
			root: null,
			rootMargin: '-20% 0px -70% 0px',
			threshold: 0,
		};

		const observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				const index = Array.from( sections ).indexOf( entry.target );
				if ( index === -1 ) return;

				const navLink = navLinks[ index ];
				if ( ! navLink ) return;

				const indicator = navLink.querySelector( '.nav-indicator' );

				if ( entry.isIntersecting ) {
					// Remove active class from all links
					navLinks.forEach( ( link ) => {
						link.classList.remove( 'active', 'text-mc-blue-400' );
						link.classList.add( 'text-mc-black-100' );
						const ind = link.querySelector( '.nav-indicator' );
						if ( ind )
							gsap.to( ind, { width: '0', duration: 0.3 } );
					} );

					// Add active class to current link
					navLink.classList.add( 'active', 'text-mc-blue-400' );
					navLink.classList.remove( 'text-mc-black-100' );
					if ( indicator )
						gsap.to( indicator, { width: '100%', duration: 0.3 } );

					// Scroll the navigation to center the active item
					scrollNavIntoView( navLink );

					// Also preload the next image if it exists
					if ( index + 1 < sections.length ) {
						// Find the next section's image
						const nextSection = sections[ index + 1 ];
						if ( nextSection ) {
							const nextImg = nextSection.querySelector( 'img' );
							if ( nextImg && nextImg.src ) {
								console.log(
									`Mobile: Preloading next image for section ${
										index + 1
									}`
								);
								preloadImage( nextImg.src ).catch( ( err ) =>
									console.warn(
										`Mobile: Failed to preload image: ${
											err.message || 'Unknown error'
										}`
									)
								);
							}
						}
					}
				}
			} );
		}, observerOptions );

		// Observe all sections
		sections.forEach( ( section ) => {
			observer.observe( section );
		} );

		// Navigation click handling
		navLinks.forEach( ( link ) => {
			link.addEventListener( 'click', ( e ) => {
				e.preventDefault();

				// Try data-section first, fall back to href without the # if needed
				let targetId = link.getAttribute( 'data-section' );
				if ( ! targetId ) {
					const href = link.getAttribute( 'href' );
					if ( href && href.startsWith( '#' ) ) {
						targetId = href.substring( 1 );
					}
				}

				if ( ! targetId ) return;

				const targetSection = document.getElementById( targetId );

				if ( targetSection ) {
					console.log( `Mobile scrolling to section ${ targetId }` );

					// Preload the target section image
					const index =
						Array.from( sections ).indexOf( targetSection );

					// Find the image directly from the target section
					const targetImg = targetSection.querySelector( 'img' );
					if ( targetImg && targetImg.src ) {
						console.log(
							`Mobile navigation: Preloading image for section ${ index }`
						);
						preloadImage( targetImg.src ).catch( ( err ) =>
							console.warn(
								`Mobile navigation: Failed to preload target image: ${
									err.message || 'Unknown error'
								}`
							)
						);
					}

					targetSection.scrollIntoView( {
						behavior: 'smooth',
						block: 'start',
					} );

					// Also scroll the nav to center this item
					scrollNavIntoView( link );
				} else {
					console.warn(
						`Target section with ID ${ targetId } not found`
					);
				}
			} );
		} );

		// Cleanup function
		return () => {
			observer.disconnect();
		};
	} );
} );
